import { dateTimeDisplayFormat, formatApiDate, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { t } from 'i18next';
import { formatCurrency } from '@/helpers/format';
import { Button, Col, DatePicker, Form, Row, Select, Space, Spin } from 'antd';
import React from 'react';
import ReportItem from './components/ReportItem';
import { Bar, Line } from 'react-chartjs-2';
import { range, sortBy } from 'lodash';
import { ChartData, ChartOptions } from 'chart.js';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  Filler,
} from 'chart.js';
import { SearchOutlined } from '@ant-design/icons';
import { CashierReportType } from '@/constants';
import { apiSdk } from '@/httpClient';
import { CompanyDashboardReportModel } from '@/__generated';
import moment from 'moment';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Title, Tooltip, Legend, ArcElement, BarElement);

export const lineOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,

  plugins: {
    legend: {
      display: true,
    },
    title: {
      display: false,
    },

    tooltip: {
      position: 'nearest',
      mode: 'index',
      intersect: false,
      padding: 10,
      caretSize: 4,
      borderWidth: 2,
    },
  },
};
export const barOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
    },
    title: {
      display: false,
    },

    tooltip: {
      position: 'nearest',
      mode: 'index',
      intersect: false,
      padding: 10,
      caretSize: 4,
      borderWidth: 2,
    },
  },
};
const colors = ['#ffe2e6', '#fff4de', '#dcfce7', '#f4e8ff'];
let labels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
//
const years = range(0, 10)
  .map(x => new Date().getFullYear() - x)
  .map(x => ({ label: x, value: x }));
export const reportColors = [
  '#ffcfc9',
  '#009688',
  '#c6dae1',
  '#ffe5a1',
  '#ff5722',
  '#ffc8aa',
  '#d4edbe',
  '#bee1f5',
  '#e5cff1',
  '#e7ebed',
  '#8f5d07',
  '#61affe',
];

export default function CompanyDashboard() {
  const [form] = Form.useForm<any>();
  const [loading, setIsLoading] = React.useState(false);
  const [params, setParams] = React.useState({ type: CashierReportType.Year, startDate: moment().add(-7, 'd'), year: new Date().getFullYear() });
  const [report, setReport] = React.useState<CompanyDashboardReportModel>();

  React.useEffect(() => {
    setIsLoading(true);
    apiSdk.CompanyDashboardReportService.getReports(params as any)
      .then(res => {
        setReport(res);
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setIsLoading(false);
      });
  }, [params]);
  const summaryReport = React.useMemo(() => {
    return [
      {
        label: t('Revenue'),
        value: formatCurrency(report?.summaryReport?.revenue || 0),
      },
      {
        label: t('Tickets'),
        value: formatCurrency(report?.summaryReport?.ticketAmount),
      },
      {
        label: t('Shipments'),
        value: formatCurrency(report?.summaryReport?.shipmentAmount),
      },
      {
        label: t('Ticket Sold'),
        value: report?.summaryReport?.noOfTicketSold || 0,
      },
      {
        label: t('Over The Counters'),
        value: report?.summaryReport?.noOfCashierTickets || 0,
      },
      {
        label: t('Online'),
        value: report?.summaryReport?.noOfOnlineTickets || 0,
      },
      {
        label: t('Shipments Handled'),
        value: report?.summaryReport?.noOfShipments || 0,
      },
      {
        label: t('Expenses'),
        value: formatCurrency(report?.summaryReport?.expense || 0),
      },
      {
        label: t('Bagages'),
        value: report?.summaryReport?.noOfBaggage || 0,
      },
    ];
  }, [report]);

  const ticketAmountReport = React.useMemo(() => {
    if (report?.ticketBookingReport?.monthlyReports?.length) {
      let monthlyReports = sortBy(report?.ticketBookingReport?.monthlyReports || [], x => x.month);
      return {
        labels: labels,

        datasets: [
          {
            label: t('Cashier Booking'),
            borderColor: reportColors[1],
            backgroundColor: reportColors[1],
            data: monthlyReports?.map(x => x.cashierTicketAmount),
          },
          {
            label: t('Online Booking'),
            borderColor: reportColors[4],
            backgroundColor: reportColors[4],
            data: monthlyReports?.map(x => x.onlineTicketAmount),
          },
        ],
      };
    }
    let dailyReports = sortBy(report?.ticketBookingReport?.dailyReports || [], x => moment(x.date));

    return {
      labels: dailyReports.map(x => dateTimeDisplayFormat.dateOnly(x.date)),
      datasets: [
        {
          label: t('Cashier Booking'),
          borderColor: reportColors[1],
          backgroundColor: reportColors[1],
          data: dailyReports?.map(x => x.cashierTicketAmount),
        },
        {
          label: t('Online Booking'),
          borderColor: reportColors[4],
          backgroundColor: reportColors[4],
          data: dailyReports?.map(x => x.onlineTicketAmount),
        },
      ],
    };
  }, [report]);

  const ticketReport = React.useMemo(() => {
    if (report?.ticketBookingReport?.monthlyReports?.length) {
      let monthlyReports = sortBy(report?.ticketBookingReport?.monthlyReports || [], x => x.month);
      return {
        labels: labels,
        datasets: [
          {
            label: t('Cashier Booking'),
            borderColor: reportColors[0],
            backgroundColor: reportColors[0],
            data: monthlyReports?.map(x => x.noOfCashierTickets),
          },
          {
            label: t('Online Booking'),
            borderColor: reportColors[7],
            backgroundColor: reportColors[7],
            data: monthlyReports?.map(x => x.noOfOnlineTickets),
          },
        ],
      };
    }
    let dailyReports = sortBy(report?.ticketBookingReport?.dailyReports || [], x => moment(x.date));

    return {
      labels: dailyReports.map(x => dateTimeDisplayFormat.dateOnly(x.date)),
      datasets: [
        {
          label: t('Cashier Booking'),
          borderColor: reportColors[0],
          backgroundColor: reportColors[0],
          data: dailyReports?.map(x => x.noOfCashierTickets),
        },
        {
          label: t('Online Booking'),
          borderColor: reportColors[7],
          backgroundColor: reportColors[7],
          data: dailyReports?.map(x => x.noOfOnlineTickets),
        },
      ],
    };
  }, [report]);

  const passengerReport = React.useMemo(() => {
    if (report?.passengerReport?.monthlyReports?.length) {
      let monthlyReports = sortBy(report?.passengerReport?.monthlyReports || [], x => x.month);
      return {
        labels: labels,
        datasets: [
          {
            label: t('Adult'),
            borderColor: reportColors[0],
            backgroundColor: reportColors[0],
            data: monthlyReports?.map(x => x.noOfAdult),
          },
          {
            label: t('Child'),
            borderColor: reportColors[7],
            backgroundColor: reportColors[7],
            data: monthlyReports?.map(x => x.noOfChild),
          },
          {
            label: t('Infant'),
            borderColor: reportColors[9],
            backgroundColor: reportColors[9],
            data: monthlyReports?.map(x => x.noOfInfant),
          },
        ],
      };
    }
    let dailyReports = sortBy(report?.passengerReport?.dailyReports || [], x => moment(x.date));
    return {
      labels: dailyReports.map(x => dateTimeDisplayFormat.dateOnly(x.date)),
      datasets: [
        {
          label: t('Adult'),
          borderColor: reportColors[0],
          backgroundColor: reportColors[0],
          data: dailyReports?.map(x => x.noOfAdult),
        },
        {
          label: t('Child'),
          borderColor: reportColors[7],
          backgroundColor: reportColors[7],
          data: dailyReports?.map(x => x.noOfChild),
        },
        {
          label: t('Infant'),
          borderColor: reportColors[9],
          backgroundColor: reportColors[9],
          data: dailyReports?.map(x => x.noOfInfant),
        },
      ],
    };
  }, [report]);

  const expenseReport = React.useMemo(() => {
    return {
      labels: labels,
      datasets: [
        {
          label: t('Expense'),
          borderColor: reportColors[1],
          backgroundColor: reportColors[1],
          data: range(0, 12).map(x => 0),
        },
      ],
    };
  }, [report]);

  let revenueReport = React.useMemo(() => {
    if (report?.revenueReport?.monthlyReports?.length) {
      let revenue = sortBy(report?.revenueReport?.monthlyReports || [], x => x.month);
      return {
        labels: labels,
        datasets: [
          {
            label: t('Tickets'),
            borderColor: reportColors[11],
            backgroundColor: reportColors[11],
            data: revenue.map(x => x.value),
            fill: true,
            tension: 0.2,
          },
        ],
      } as ChartData<'line', (number | undefined)[], string>;
    }
    let revenue = sortBy(report?.revenueReport?.dailyReports || [], x => moment(x.date));
    return {
      labels: revenue.map(x => dateTimeDisplayFormat.dateOnly(x.date)),
      datasets: [
        {
          label: t('Tickets'),
          borderColor: reportColors[11],
          backgroundColor: reportColors[11],
          data: revenue.map(x => x.value),
          fill: true,
          tension: 0.2,
        },
      ],
    } as ChartData<'line', (number | undefined)[], string>;
  }, [report]);

  const onSearch = (v: any) => {
    setParams({
      ...v,
      startDate: formatApiDate(v.startDate),
    });
  };
  const reportType = Form.useWatch('type', form);

  return (
    <Spin spinning={loading}>
      <div style={{ minHeight: '100%', backgroundColor: '#f2f3f5', paddingBottom: 20 }}>
        <div className="app-page-title">
          <div style={{ backgroundColor: 'white' }}>
            <div className="title-header">
              <div className="title">{t('Report')}</div>

              <div className="extra-btns">
                <Form onFinish={onSearch} layout="inline" initialValues={params} form={form}>
                  <Row gutter={ROW_GUTTER}>
                    <Space>
                      <Form.Item label={t('Type')} name={'type'}>
                        <Select options={CashierReportType.__getSelectOptions()} style={{ width: 150 }} />
                      </Form.Item>
                      {reportType === CashierReportType.Year ? (
                        <Form.Item label={t('Year')} name="year">
                          <Select style={{ width: 150 }} options={years} />
                        </Form.Item>
                      ) : (
                        <Form.Item label={t('Start date')} name="startDate" rules={[{ required: true, message: undefined }]}>
                          <DatePicker allowClear={false} />
                        </Form.Item>
                      )}

                      <Button icon={<SearchOutlined />} type="primary" htmlType="submit">
                        {t('zTable.search')}
                      </Button>
                    </Space>
                  </Row>
                </Form>
              </div>
            </div>
            <div style={{ minHeight: 100, padding: 10 }}>
              <Row gutter={[ROW_GUTTER, ROW_GUTTER]}>
                <Col md={12}>
                  <ReportItem title={t('Shipment, Tickets & Baggage Details')}>
                    <Row gutter={[ROW_GUTTER, ROW_GUTTER]}>
                      {summaryReport.map((r, i) => {
                        return (
                          <Col md={12} xl={12} xxl={8} key={r.label}>
                            <div style={{ background: colors[i % 4], borderRadius: 10, padding: '15px 10px' }}>
                              <div style={{ fontSize: 24, fontWeight: 'bold' }}>{r.value}</div>
                              <div style={{ fontSize: 18, fontWeight: 500 }}>{r.label}</div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </ReportItem>
                </Col>
                <Col md={12}>
                  <ReportItem title={t('Bookings Amount')}>
                    <Bar options={barOptions} data={ticketAmountReport} height={400} />
                  </ReportItem>
                </Col>
              </Row>
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <Row gutter={[20, 20]}>
              <Col md={12}>
                <ReportItem title={t('No. of Bookings')}>
                  <Bar options={barOptions} data={ticketReport} height={400} />
                </ReportItem>
              </Col>
              <Col md={12}>
                <ReportItem title={t('No. of Passengers')}>
                  <Bar options={barOptions} data={passengerReport} height={400} />
                </ReportItem>
              </Col>
              <Col md={12}>
                <ReportItem title={t('Revenue')}>
                  <Line options={lineOptions} data={revenueReport} height={400} />
                </ReportItem>
              </Col>

              <Col md={12}>
                <ReportItem title={t('Expense Detail')}>
                  <Bar options={barOptions} data={expenseReport} height={400} />
                </ReportItem>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </Spin>
  );
}
